'use client';

import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useStore, useStoreApi } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { ChatHeader } from './ChatHeader';
import { ChatInput, ChatInputDisclaimer } from './ChatInput';
import { MediaBrowser } from './MediaBrowser/index';
// import { SkillSceneCard, type SkillSceneKey } from './SkillSceneCard';
import type { ChatSceneCtx } from './use-chat-scene';
import { Welcome } from './Welcome/index';

interface WelcomeChatPanelProps {
  ctx: ChatSceneCtx;
}

/**
 * 欢迎态场景容器：`/claw` 路径无 sessionsId 时渲染。
 *
 * 仅承载欢迎页 hero / quick case / 首条消息输入；
 * history loading / messages list / TaskPanel / SubtaskPanel / StoryDetail
 * 等对话态副作用全部留给 ConversationChatPanel。
 *
 * 共享的布局壳（ChatHeader、dropzone、MediaBrowser）仍然保留，
 * 让用户在欢迎页也能拖拽上传 / 浏览已有素材。
 */
export const WelcomeChatPanel = ({ ctx }: WelcomeChatPanelProps) => {
  const t = useTranslations();
  const pendingClawInput = useStore((state) => state.pendingClawInput);
  const storeApi = useStoreApi();
  // SkillSceneCard 的选中态：默认不选中，点击卡片才高亮；
  // 输入框草稿被整体清空（删除 skill / 极速模式清空 / 点 quality case / 发送成功等）时同步取消高亮。
  // const [selectedSkillKey, setSelectedSkillKey] = useState<SkillSceneKey | null>(null);
  const {
    chatContainerRef,
    chatScrollRef,
    chatInputRef,
    isDraggingOver,
    isShowFile,
    pendingPreviewFilePath,
    setPendingPreviewFilePath,
    restoringSessionMedia,
    bumpClawFileListRefreshKey,
    chatInputSending,
    chatInputOnSend,
    onApplyForAccess,
    disableChatInputSend,
    disableChatInputUpload,
    chatInputYoloActive,
    showChatInput,
    handlePreviewFile,
    handleChatScroll,
  } = ctx;

  useEffect(() => {
    if (!pendingClawInput) return;
    const pending = pendingClawInput;
    storeApi.getState().setPendingClawInput(null);

    const tryApply = () => {
      if (!chatInputRef.current) {
        requestAnimationFrame(tryApply);
        return;
      }
      const quickPrompt = {
        prompt: pending.prompt,
        assets: pending.referenceImageUrl ? [{ url: pending.referenceImageUrl, type: 'image' as const }] : undefined,
      };
      chatInputRef.current.applyQuickPrompt(quickPrompt);
    };
    tryApply();
  }, [pendingClawInput]);

  return (
    <div className="flex h-full min-h-0 p-4 pt-2">
      <div
        ref={chatContainerRef}
        className={cn(
          'relative flex h-full w-full flex-col rounded-16 bg-system-white08 light:bg-[rgba(255,255,255,0.54)]',
          isShowFile ? 'w-3/5' : 'w-full',
        )}
      >
        {isDraggingOver && (
          <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-16 border-2 border-[#508DFF] border-dashed bg-[rgba(80,141,255,0.06)]">
            <span className="text-[#508DFF] text-base">{t('create.dragUpload.placeholder')}</span>
          </div>
        )}
        <ChatHeader />
        <div className={cn('relative flex h-full min-h-0 min-w-0 flex-col')}>
          <div ref={chatScrollRef} className="min-h-0 flex-1 overflow-y-auto" onScroll={handleChatScroll}>
            {/* 欢迎页没有 sessionId，不需要任何 history loading / empty 兜底， 否则会先闪一下骨架或空态再切到欢迎页。 */}
            <Welcome
              chatInputRef={chatInputRef}
              onResetInput={() => {
                // 点 quality case 视为换内容：先清空当前输入草稿（含 skill 槽 / 附件），
                // resetInput 会回调 onInputReset 同步取消 SkillSceneCard 高亮。
                chatInputRef.current?.resetInput();
              }}
            />
          </div>
          {showChatInput && (
            <div className="relative flex min-h-32 flex-col items-center gap-4 p-4 pt-0">
              <div className={cn('relative w-full', isShowFile ? 'max-w-[960px]' : 'max-w-[1180px] lg:max-w-full')}>
                <div className="pointer-events-none h-4 w-full bg-[linear-gradient(180deg,rgba(29,32,35,0)_0%,#1D2023_100%)] light:bg-[linear-gradient(180deg,rgba(244,244,250,0)_0%,rgba(244,244,250,1)_100%)]" />
                {/* <SkillSceneCard
                  selectedKey={selectedSkillKey}
                  onSelect={(item, key) => {
                    setSelectedSkillKey(key ?? null);
                    chatInputRef.current?.applyQuickPrompt(item);
                  }}
                /> */}
                <ChatInput
                  ref={chatInputRef}
                  sending={chatInputSending}
                  onSend={chatInputOnSend}
                  onApplyForAccess={onApplyForAccess}
                  disableSend={disableChatInputSend}
                  disableUpload={disableChatInputUpload}
                  yoloActive={chatInputYoloActive}
                  onUploadSuccess={() => {
                    bumpClawFileListRefreshKey();
                  }}
                  // onInputReset={() => setSelectedSkillKey(null)}
                  onPreviewFile={handlePreviewFile}
                />
              </div>
              <ChatInputDisclaimer />
            </div>
          )}
        </div>
      </div>
      {isShowFile ? (
        <div className="w-2/5 min-w-[360px]">
          <MediaBrowser
            previewTargetFilePath={pendingPreviewFilePath}
            onPreviewHandled={() => setPendingPreviewFilePath(null)}
            syncing={restoringSessionMedia}
            onInsertAsset={(asset) => {
              chatInputRef.current?.insertAssetMention(asset);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
